import { ButtonCusWithIcon } from '@/components/common/Button';
import Tooltip from '@/components/common/Tooltip';
import { DISPPLAY_MOMENT_YMD_HM, ekDomain, ORDER_STATUS_EU, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { DOWNLOAD_TYPE, DownloadType, ORDER_STATUS, PAYMENT_METHOD } from '@/types/enums';
import { ReservationListItem } from '@/types/reservation';
import { formatDateTime } from '@/utils/datetime';
import { formatCurrency } from '@/utils/formatNumber';
import { cn, convertDateTime } from '@/utils/utils';
import classNames from 'classnames';
import { Calendar, Clock9 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import IssueInvoiceFormRentalHistory from './IssueInvoiceFormRentalHistory';
import { isPathEk } from '@/utils/routes';
import CancelOrder from '@/pages/reservation-cms/detail/modal/CancelOrder';
import { InvoiceIcon, ReceiptIcon } from '@/components/icon/bill-icons';

export default observer(function RentalHistoryItem({ reservation }: { reservation: ReservationListItem }) {
    // hocks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // store
    const {
        reservationStore: { getReservationENDList },
        modalStore: { showAlertModal, showModal }
    } = useStore();

    // const
    const { day, date, time } = convertDateTime(reservation.orderDate);

    // function
    const onClickDownloadInvoice = async (e, id: number) => {
        // e.stopPropagation();
        handleOpenModal(reservation, DOWNLOAD_TYPE.INVOICE);
        // await flowResult(downloadInvoice({ id }));
    };

    const onClickDownloadReceipt = async (e, id: number) => {
        // e.stopPropagation();
        handleOpenModal(reservation, DOWNLOAD_TYPE.RECEIPT);
        // await flowResult(downloadReceipt({ id }));
    };

    const handleOpenModal = (item: ReservationListItem, type: DownloadType) => {
        const idModal = 'issue-invoice-modal';
        let data = {
            id: item.id,
            renter: {
                id: item?.renter?.id,
                fullName: item?.renter?.fullname
            },
            acceptDownloadReceipt: item.hasDownloadReceipt
        };
        showAlertModal({
            id: idModal,
            // title: t('words_title.rental_bike_fee_table_issued'),
            title: type === DOWNLOAD_TYPE.RECEIPT ? '領収書発行' : '請求書発行',
            size: '2xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <IssueInvoiceFormRentalHistory idModal={idModal} adjust={data} type={type} isAdmin={false} />
            ),
            footerClassName: 'hidden'
        });
    };

    const handleClickCancel = (orderId) => {
        showModal({
            id: 'cancel-order',
            size: '2xl',
            title: t('words_title.cancel_reservation'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0',
            content: (
                <CancelOrder
                    orderIds={[orderId]}
                    reload={() => {
                        getReservationENDList();
                    }}
                    isForEnd
                />
            )
        });
    };

    return (
        <div
            className='flex flex-col gap-4 sm:p-4 p-3 border rounded-base'
        >
            {/* Day */}
            <div className='flex flex-col gap-y-4 sm:gap-y-0 sm:gap-x-2 gap-x-0 sm:flex-row sm:justify-between sm:items-center'>
                <div className='flex order-2 sm:order-1'>
                    <div className='flex gap-2 items-center'>
                        <Calendar strokeWidth={1.5} />
                        <span>{(day && date) ? day + `, ${date}` : '--'}</span>
                    </div>
                    <div className='flex gap-2 ml-4 items-center'>
                        <Clock9 strokeWidth={1.5} />
                        <span>{time || '--'}</span>
                    </div>

                </div>
                <div className='flex justify-between gap-2 order-1 sm:order-2 flex-1'>
                    <ButtonCusWithIcon
                        className='order-2 sm:order-1 gap-1'
                        variant='outline'
                        size='xsm'
                        onClick={() => {
                            // window.location.href = `${window.location.href}/${reservation.id}`;
                            navigate(`${pathname}/${reservation.id}`);
                        }}
                        iconId='#icon-search'
                    >
                        {t('button.view_detail')}
                    </ButtonCusWithIcon>
                    <div className={`py-1 px-5 ${ORDER_STATUS_EU[reservation.orderStatus]?.bgColor} text-white rounded-full w-fit order-1 sm:order-2`}>
                        {t(`words_title.${ORDER_STATUS_EU[reservation.orderStatus]?.key}`)}
                    </div>
                </div>
            </div>
            <div className='border-t border-dashed'></div>
            {/* ID */}
            <div className='xsm:text-[1.0625rem] point-375:text-[1rem] text-fs-mid18 text-[#3B7CEC] font-medium flex gap-1'>
                <p>{t('field.reservation_code_2')}</p>
                <span
                    className='cursor-pointer hover:underline'
                    onClick={() => {
                        navigate(`${pathname}/${reservation.id}`);
                    }}
                >
                    {reservation.orderCodeSystem}
                </span>
            </div>
            {/* Content */}
            <div className='grid grid-cols-1 xsm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
                <FieldItem lable={t('field.reservation.rental_time')} value={formatDateTime(reservation?.rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || '--'} />
                <FieldItem lable={t('field.reservation.return_time')} value={formatDateTime(reservation?.rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || '--'} />
                <FieldItem
                    lable={t('field.location')}
                    value={reservation?.location?.name || '--'}
                    textValueStyle='text-[#3B7CEC] hover:underline cursor-pointer'
                    onClick={() => {
                        navigate(isPathEk(pathname) ?
                            `/${ekDomain}/${ROUTES.locationList.hrefEK}/${ROUTES.locationDetailEnduser.hrefEK}/${reservation?.location?.id}` :
                            `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${reservation?.location?.id}`);
                    }}
                />
                <FieldItem
                    lable={t('field.model_name')}
                    value={reservation?.bikeLicense?.bikeModel || '--'}
                    textValueStyle='text-[#3B7CEC] hover:underline cursor-pointer'
                    onClick={() => {
                        navigate(isPathEk(pathname) ?
                            `/${ekDomain}/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${reservation?.location?.id}/${ROUTES.bikeBookingDetail.href}/${reservation?.bikeLicense.id}` :
                            `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${reservation?.location?.id}/${ROUTES.bikeBookingDetail.href}/${reservation?.bikeLicense.id}`);
                    }}
                />
                <FieldItem lable={t('field.payment')} value={`${t('currency.currency_symbol')}${formatCurrency(reservation.payment.totalPayment) || '0'}`} />
                <FieldItem lable={t('field.payment_method')} value={reservation.payment.method || '--'} />
                <div className='grid grid-cols-2 xsm:grid-cols-2 col-span-full xsm:col-span-2 gap-4'>
                    <div>
                        <p className='font-medium text-[#767676]'>{t('field.invoice')}</p>
                        <div className='size-9 rounded-full bg-[#F6F6F6] mt-2 flex items-center justify-center'>
                            <Tooltip
                                content={t('button.download_invoice')}
                                triggerClassName={classNames((reservation.orderStatus !== ORDER_STATUS.Cancel &&
                                    (reservation?.hasAdditionalCharge === true || reservation.payment.paymentMethodVal === PAYMENT_METHOD.VISUAL_TRANSFER)) ?
                                    'cursor-pointer' :
                                    'cursor-not-allowed pointer-events-none')}
                            >
                                <div
                                    onClick={e => onClickDownloadInvoice(e, reservation.id)}
                                    className={classNames(
                                        'size-9 rounded-full  flex items-center justify-center transition-all ',
                                        (reservation.orderStatus !== ORDER_STATUS.Cancel &&
                                            (reservation?.hasAdditionalCharge === true || reservation.payment.paymentMethodVal === PAYMENT_METHOD.VISUAL_TRANSFER)) ?
                                            'bg-background-4 hover:bg-gray-200' :
                                            'bg-disable-2'

                                    )}
                                >
                                    <InvoiceIcon className='size-5 text-icon-4' />
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div>
                        <p className='font-medium text-[#767676]'>{t('field.receipt')}</p>
                        <div className='size-9 rounded-full bg-[#F6F6F6] mt-2 flex items-center justify-center'>
                            <Tooltip
                                content={t('button.download_receipt')}
                                triggerClassName={classNames(reservation.hasDownloadReceipt ? '' : 'cursor-not-allowed pointer-events-none')}
                            >
                                <div
                                    onClick={e => onClickDownloadReceipt(e, reservation?.id)}
                                    className={classNames(
                                        'size-9 rounded-full bg-background-4 flex items-center justify-center hover:bg-gray-200 transition-all',
                                        reservation.hasDownloadReceipt ? 'bg-background-4 hover:bg-gray-200' : 'bg-disable-2'
                                    )}
                                >
                                    <ReceiptIcon className='size-5 text-icon-4' />
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                </div>

            </div>
            {/* Btn */}
            <div className='flex md:gap-4 gap-2 xsm:justify-start justify-center flex-wrap'>
                {
                    reservation.orderStatus !== ORDER_STATUS.Cancel && (
                        <ButtonCusWithIcon
                            type='button'
                            variant='green'
                            size='md'
                            disabled={!reservation?.hasDownloadReceipt}
                            className='w-full xsm:w-[208px]'
                            onClick={() => handleOpenModal(reservation, DOWNLOAD_TYPE.RECEIPT)}
                            iconId='#icon-invoice'
                        >
                            {t('button.issue_receipt')}
                        </ButtonCusWithIcon>
                    )
                }
                {
                    [ORDER_STATUS.Not_Paid.valueOf(), ORDER_STATUS.Paid.valueOf()].includes(reservation?.orderStatus) && (
                        <ButtonCusWithIcon
                            size='md'
                            className='w-full xsm:w-[208px]'
                            variant='default'
                            iconId='#icon-delete'
                            onClick={() => handleClickCancel(reservation?.id)}
                        >
                            {t('button.cancel_booking')}
                        </ButtonCusWithIcon>
                    )
                }
            </div>
        </div>
    );
});

const FieldItem = ({ textValueStyle, lable, value, onClick }: { textValueStyle?: string, lable: string, value: string, onClick?: () => void }) => {
    return (
        <div className='xsm:block flex gap-2 justify-between'>
            <p className='font-medium text-[#767676] !w-fit'>{lable}</p>
            <p
                className={cn(textValueStyle, 'font-normal break-all flex-1 line-clamp-2 xsm:text-left text-right')}
                onClick={(e) => {
                    onClick && onClick();
                    e.stopPropagation();
                    e.preventDefault();
                }}
            >
                {value}
            </p>
        </div>
    );
};
